import { useMemo } from 'react';
import { startOfWeek, addDays, format, parseISO, getISOWeek, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, CalendarRange, AlertTriangle } from 'lucide-react';
import {
  useEmployees, useSchedules, usePendingLeaves, usePendingTimeAdjustments,
  usePharmacySettings,
} from '@/api/resources';
import type { Schedule, ScheduleStatus, AbsenceType } from '@/types';
import { absenceMeta } from '@/lib/absenceTypes';
import { weekDayCount } from '@/lib/schedule';

// Le dimanche n'apparaît que si l'officine ouvre ce jour-là.
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const ABSENCE_STATUSES: ScheduleStatus[] = ['LEAVE', 'SICK', 'ACCIDENT', 'MATERNITY'];

/**
 * La demande actuellement examinée, pour la mettre en évidence dans la grille.
 * Optionnelle : sans focus, le composant sert d'aperçu « qui est là cette
 * semaine » (ouvert depuis le calendrier Disponibilités d'un salarié).
 */
export interface PreviewFocus {
  kind: 'leave' | 'adjustment';
  id: string;
  employeeId: string;
  employeeName: string;
  /** Jour de référence (une date ISO) — détermine la semaine affichée. */
  date: string;
  /** Pour un congé multi-jours : bornes (ISO), sinon = date. */
  startDate?: string;
  endDate?: string;
}

const dayHours = (s: Schedule | undefined): number => {
  if (!s || s.status !== 'WORK') return 0;
  const m = (a?: string | null, b?: string | null) => {
    if (!a || !b) return 0;
    const [ah, am] = a.split(':').map(Number);
    const [bh, bm] = b.split(':').map(Number);
    return bh + bm / 60 - (ah + am / 60);
  };
  return m(s.morningStart, s.morningEnd) + m(s.afternoonStart, s.afternoonEnd);
};

const isApprovedAbsence = (s: Schedule | undefined) =>
  !!s && (s.absenceType != null || ABSENCE_STATUSES.includes(s.status));

export function RequestWeekPreview({
  focus, date, onClose,
}: {
  /** Demande examinée — met une bordure sur les cases concernées. */
  focus?: PreviewFocus;
  /** Jour de référence quand il n'y a pas de demande (simple consultation). */
  date?: string;
  onClose: () => void;
}) {
  const focusDate = parseISO(focus?.date ?? date ?? format(new Date(), 'yyyy-MM-dd'));
  const { data: pharmacy } = usePharmacySettings();
  const dayCount = weekDayCount(pharmacy?.openingHours);
  const gridCols = `160px repeat(${dayCount}, 1fr)`;
  const weekStart = startOfWeek(focusDate, { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, dayCount - 1);
  const fromIso = format(weekStart, 'yyyy-MM-dd');
  const toIso = format(weekEnd, 'yyyy-MM-dd');
  const weekNo = getISOWeek(weekStart);

  const { data: employees } = useEmployees();
  const { data: schedules, isLoading: schedLoading } = useSchedules(fromIso, toIso);
  const { data: pendingLeaves } = usePendingLeaves();
  const { data: pendingAdj } = usePendingTimeAdjustments();

  const dayKeys = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd')),
    [weekStart, dayCount]
  );

  const scheduleMap = useMemo(() => {
    const m = new Map<string, Schedule>();
    schedules?.forEach((s) => m.set(`${s.employeeId}|${s.date.slice(0, 10)}`, s));
    return m;
  }, [schedules]);

  // Congés PENDING → une entrée par (salarié, jour) tombant dans la semaine.
  const pendingLeaveMap = useMemo(() => {
    const m = new Map<string, { type: AbsenceType; id: string }>();
    pendingLeaves?.forEach((l) => {
      const start = parseISO(l.startDate);
      const end = parseISO(l.endDate);
      dayKeys.forEach((dk) => {
        const d = parseISO(dk);
        if (isWithinInterval(d, { start, end })) m.set(`${l.employeeId}|${dk}`, { type: l.type, id: l.id });
      });
    });
    return m;
  }, [pendingLeaves, dayKeys]);

  const pendingAdjMap = useMemo(() => {
    const m = new Map<string, string>();
    pendingAdj?.forEach((a) => m.set(`${a.employeeId}|${a.date.slice(0, 10)}`, a.id));
    return m;
  }, [pendingAdj]);

  // Compteur d'absents par jour : validés + en attente (par salarié unique).
  const perDay = useMemo(() => {
    return dayKeys.map((dk) => {
      let approved = 0;
      let pending = 0;
      employees?.forEach((emp) => {
        const s = scheduleMap.get(`${emp.id}|${dk}`);
        if (isApprovedAbsence(s)) approved++;
        else if (pendingLeaveMap.has(`${emp.id}|${dk}`)) pending++;
      });
      return { approved, pending };
    });
  }, [dayKeys, employees, scheduleMap, pendingLeaveMap]);

  const focusInterval = focus?.startDate && focus?.endDate
    ? { start: parseISO(focus.startDate), end: parseISO(focus.endDate) }
    : null;
  const isFocusCell = (empId: string, dk: string) => {
    if (!focus || empId !== focus.employeeId) return false;
    if (focus.kind === 'adjustment') return dk === focus.date;
    if (focusInterval) return isWithinInterval(parseISO(dk), focusInterval);
    return dk === focus.date;
  };

  const totalPending = perDay.reduce((n, d) => n + d.pending, 0);
  const maxOffDay = Math.max(...perDay.map((d) => d.approved + d.pending), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <CalendarRange size={18} className="text-primary" />
              <h2 className="text-[16px] font-semibold tracking-tight">
                Aperçu de la semaine {weekNo}
              </h2>
            </div>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Du {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM yyyy', { locale: fr })}
              {focus
                ? <> · effet de la demande de <strong>{focus.employeeName}</strong> avec toutes les demandes en cours.</>
                : ' · qui travaille, qui est absent.'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {maxOffDay >= 3 && (
          <div className="mx-6 mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              Jusqu'à <strong>{maxOffDay} salariés absents le même jour</strong> cette semaine
              {totalPending > 0 && ' (en comptant les demandes en attente)'}. Vérifiez la couverture avant d'accepter.
            </span>
          </div>
        )}

        <div className="flex-1 overflow-auto p-6">
          {schedLoading ? (
            <div className="text-[13px] text-slate-400 py-10 text-center">Chargement de la semaine…</div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-hidden min-w-[840px]">
              {/* En-tête jours */}
              <div className="grid bg-slate-50 border-b border-slate-200" style={{ gridTemplateColumns: gridCols }}>
                <div className="px-3 py-2 text-[11px] font-medium text-slate-600 uppercase tracking-wider border-r border-slate-200">
                  Salarié
                </div>
                {dayKeys.map((dk, i) => {
                  const off = perDay[i];
                  return (
                    <div key={dk} className="px-2 py-2 text-center border-r border-slate-200 last:border-r-0">
                      <div className="text-[11px] font-medium">{DAYS[i]}</div>
                      <div className="text-[10px] text-slate-500 tabular-nums">{format(parseISO(dk), 'dd/MM')}</div>
                      {(off.approved + off.pending) > 0 && (
                        <div className={`mt-0.5 text-[9px] font-medium ${off.approved + off.pending >= 3 ? 'text-red-600' : 'text-slate-500'}`}>
                          {off.approved + off.pending} absent{off.approved + off.pending > 1 ? 's' : ''}
                          {off.pending > 0 && <span className="text-amber-600"> ({off.pending} en attente)</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Lignes salariés */}
              {employees?.map((emp, idx) => (
                <div
                  key={emp.id}
                  className={`grid border-b border-slate-100 last:border-b-0 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div className="px-3 py-2 border-r border-slate-200 flex items-center gap-2">
                    <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: emp.color }} />
                    <div className="min-w-0">
                      <div className="text-[12px] font-medium truncate">{emp.firstName} {emp.lastName}</div>
                      <div className="text-[10px] text-slate-500 truncate capitalize">
                        {emp.position.replace(/_/g, ' ').toLowerCase()}
                      </div>
                    </div>
                  </div>
                  {dayKeys.map((dk) => {
                    const s = scheduleMap.get(`${emp.id}|${dk}`);
                    const pendingLeave = pendingLeaveMap.get(`${emp.id}|${dk}`);
                    const hasAdj = pendingAdjMap.has(`${emp.id}|${dk}`);
                    const focused = isFocusCell(emp.id, dk);
                    const ring = focused ? 'ring-2 ring-primary ring-offset-1' : '';

                    // 1. Demande de congé en attente (tentative) — priorité visuelle.
                    if (pendingLeave) {
                      const meta = absenceMeta(pendingLeave.type);
                      return (
                        <div key={dk} className="p-1 border-r border-slate-100 last:border-r-0">
                          <div
                            title={`Demande en attente — ${meta.label}`}
                            className={`h-full min-h-[40px] rounded border border-dashed flex flex-col items-center justify-center text-[10px] font-medium text-center px-1 ${meta.cell} ${ring}`}
                            style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,.45) 0 4px, transparent 4px 8px)' }}
                          >
                            {meta.short}
                            <span className="text-[8px] opacity-70">en attente</span>
                          </div>
                        </div>
                      );
                    }

                    // 2. Absence déjà validée.
                    if (isApprovedAbsence(s)) {
                      const meta = absenceMeta(s!.absenceType);
                      return (
                        <div key={dk} className="p-1 border-r border-slate-100 last:border-r-0">
                          <div className={`h-full min-h-[40px] rounded border flex items-center justify-center text-[10px] font-medium text-center px-1 ${meta.cell} ${ring}`}>
                            {meta.short}
                          </div>
                        </div>
                      );
                    }

                    // 3. Jour travaillé.
                    const h = dayHours(s);
                    if (s?.status === 'WORK' && h > 0) {
                      return (
                        <div key={dk} className="p-1 border-r border-slate-100 last:border-r-0">
                          <div className={`relative h-full min-h-[40px] rounded border bg-primary-bg text-primary border-primary/25 flex items-center justify-center text-[11px] font-semibold tabular-nums ${ring}`}>
                            {h.toFixed(1).replace('.0', '')}h
                            {hasAdj && (
                              <span title="Correction d'horaire en attente" className="absolute top-0 right-0 rounded-bl bg-accent/20 border-l border-b border-accent/40 px-1 text-[8px] font-semibold text-accent">
                                ±
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // 4. Repos / fermé.
                    return (
                      <div key={dk} className="p-1 border-r border-slate-100 last:border-r-0">
                        <div className={`h-full min-h-[40px] rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-[10px] text-slate-300 ${ring}`}>
                          —
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Légende */}
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-600">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary-bg border border-primary/25" />Travaille</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-200 border border-slate-400" />Absence validée</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-dashed border-slate-400" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0 3px, #f8fafc 3px 6px)' }} />Demande en attente</span>
            {focus && <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-primary" />Demande examinée</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
